import { Component, Inject, Input, OnInit, Optional } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { APP_STORE_CONFIG_TOKEN } from '@ddt-web/config';
import { InjectConfig, SharedConfigKey } from '@ddt-web/config-layer';
import { OktaConstants } from '@ddt-web/core';
import { AppStoreConfig } from '@ddt-web/data-service';
import { DialogConfig, DialogService } from '@ddt-web/dialog';
import { NumberUtil, ObjectUtil } from '@ddt-web/generic-utils';
import { ApplicationStateStoreProxyService, PerformanceState } from '@ddt-web/shared-stores';
import { StoreService } from '@ddt-web/store-service';
import { FeeQueryParamKey, UrlService } from '@ddt-web/url';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import * as isNaN from 'lodash/isNaN';
import * as isNil from 'lodash/isNil';
import { first, take } from 'rxjs/operators';

import { CustomAdvisoryFee } from '../../config';
import { AdvisoryFee } from '../../models';

@UntilDestroy()
@Component({
	selector: 'cfe-custom-advisory-fee-dialog',
	templateUrl: './customize-advisory-fee-dialog.component.html',
	styleUrls: ['./customize-advisory-fee-dialog.component.scss']
})
export class CustomizeAdvisoryFeeDialogComponent implements OnInit {
	@InjectConfig(SharedConfigKey.CUSTOM_ADVISORY_FEE) public readonly customAdvisoryFee: AdvisoryFee = CustomAdvisoryFee;
	@InjectConfig(SharedConfigKey.ENABLE_SCENARIO_ADVISORY_FEE) public readonly enableDisclaimerNote: boolean = false;

	@Input() public inputConfig: DialogConfig;

	public userInputAdvisoryFee: number = 0;
	public isAdvisoryFeeValid: boolean = false;
	public isAdvisoryFeeEmpty: boolean = true;
	public disableFeeExclusion: boolean = false;
	public hasExistingCustomAdvisoryFee: boolean = false;
	public includeCustomFee: boolean = true;
	public customFee = new UntypedFormControl();
	public disclaimerNoteDCR: string = 'CUSTOM_FEE_DIALOG_NOTE_YIELD_FALSE';

	constructor(
		@Optional() public config: DialogConfig,
		private router: Router,
		private route: ActivatedRoute,
		private dialogService: DialogService,
		private applicationStateProxyService: ApplicationStateStoreProxyService,
		private urlService: UrlService,
		private storeService: StoreService,
		@Inject(APP_STORE_CONFIG_TOKEN) private storeConfig: AppStoreConfig
	) { }

	public ngOnInit(): void {
		if (this.enableDisclaimerNote && !this.inputConfig) {
			this.storeService.selectAll(this.storeConfig.reportConfig)
				.pipe(take(1))
				.subscribe(data => {
					this.disclaimerNoteDCR = ('CUSTOM_FEE_DIALOG_NOTE_YIELD_' + (data?.['includeScenarioYield'] ?? 'false')).toUpperCase();
				});
		}

		this.disableFeeExclusion = this.config?.data?.disableFeeExclusion || this.inputConfig?.data?.disableFeeExclusion;

		this.initializeAdvisoryFee();

		this.customFee.valueChanges
			.pipe(untilDestroyed(this))
			.subscribe(newValue => {
				// Run the validation so that the button will appear enabled
				// we do not set userInputAdvisoryFee until we call onBlur where we will also validate with a rounded value
				this.validateAdvisoryFee(newValue);
			});
	}

	public onBlur(): void {
		const newValue = NumberUtil.precisionRound(this.customFee.value, 2);

		this.onAdvisoryFeeChange(newValue);
		this.userInputAdvisoryFee = newValue;
	}

	public onCalculateClick(): void {
		if (this.includeCustomFee) {
			this.updateFee(this.userInputAdvisoryFee);
		} else {
			this.removeCustomFee();
		}
	}

	public toggleCustomFee(): void {
		this.includeCustomFee = !this.includeCustomFee;

		if (this.includeCustomFee) {
			this.customFee.enable();
		} else {
			this.customFee.disable();
		}
	}

	private initializeAdvisoryFee(): void {
		const isEditMode = this.config?.data?.isEditMode || this.inputConfig?.data?.isEditMode;
		if (isEditMode) {
			this.initializeEditMode();
		} else {
			this.initializeLoadMode();
		}
	}

	/**
	 * This is when the fee dialog is loaded the beginning and when switching tabs (because of the way 360 is built it recreates portfolio-analyser.component).
	 * We only show the dialog if there is no ?fee or ?excludeFee query param
	 */
	private initializeLoadMode(): void {
		const feeQueryParam = Number(this.urlService.queryParams?.[FeeQueryParamKey.CUSTOM_FEE]);
		const excludeFeeQueryParam = this.urlService.queryParams?.[FeeQueryParamKey.EXCLUDE_FEE] === 'true';

		if (feeQueryParam) {
			// Update fee from query param and apply it without showing the dialog
			this.onAdvisoryFeeChange(feeQueryParam);
			this.userInputAdvisoryFee = feeQueryParam;

			const isFeeValid = !this.isAdvisoryFeeEmpty && this.isAdvisoryFeeValid;
			if (isFeeValid) {
				this.applicationStateProxyService.updateCustomAdvisoryFee({ customAdvisoryFee: this.userInputAdvisoryFee, includeFee: true } as PerformanceState);
				this.hasExistingCustomAdvisoryFee = true;
				this.dialogService.close();
			}
		} else if (excludeFeeQueryParam) {
			this.removeCustomFee();
		} else {

			this.applicationStateProxyService.getPerformanceState().pipe(first()).subscribe((performanceState: PerformanceState) => {
				const storedFee = performanceState?.customAdvisoryFee;
				const includeFee = performanceState?.includeFee;

				// Hide the dialog (via updateFee) if fee is excluded or fee already exists
				// This is mainly for when you switch between tabs
				if (includeFee === false) {
					this.updateFee(null);
				} else if (storedFee) {
					this.updateFee(storedFee);
				} else {
					// Show default values at the beginning
					this.includeCustomFee = true;
					this.onAdvisoryFeeChange(this.customAdvisoryFee.defaultFee);
					this.userInputAdvisoryFee = this.customAdvisoryFee.defaultFee;
				}
			});
		}
	}

	/**
	 * This is edit mode - when you click on the button to edit the fee.
	 * In this mode, we always show the dialog
	 */
	private initializeEditMode(): void {
		this.applicationStateProxyService.getPerformanceState().pipe(first()).subscribe((performanceState: PerformanceState) => {
			const storedFee = performanceState?.customAdvisoryFee;
			const includeFee = performanceState?.includeFee;

			this.includeCustomFee = includeFee ?? true;

			const fee = storedFee ?? this.customAdvisoryFee.defaultFee;
			this.onAdvisoryFeeChange(fee);
			this.userInputAdvisoryFee = fee;
		});
	}

	private removeCustomFee(): void {
		this.userInputAdvisoryFee = null;
		this.updateFee(this.userInputAdvisoryFee);
	}

	private updateFee(fee: number): void {
		this.applicationStateProxyService.updateCustomAdvisoryFee({ customAdvisoryFee: fee, includeFee: fee !== null } as PerformanceState);
		this.router.navigate([], {
			relativeTo: this.route,
			queryParams: {
				[FeeQueryParamKey.CUSTOM_FEE]: fee?.toString(),
				[FeeQueryParamKey.EXCLUDE_FEE]: isNil(fee) ? true : null, // persist fee in the url
				// kill okta params
				...ObjectUtil.arrayToObject(OktaConstants.QUERY_PARAMS, null)
			},
			queryParamsHandling: 'merge'
		});

		this.dialogService.close();
	}

	private onAdvisoryFeeChange(newValue: number): void {
		this.validateAdvisoryFee(newValue);
		this.customFee.setValue(newValue.toFixed(2), { emitEvent: false });
	}

	private validateAdvisoryFee(roundedFee: number): void {
		let isWithinMinFeeConstraint = false;
		let isWithinMaxFeeConstraint = false;

		if (!isNaN(roundedFee) && !isNaN(this.customAdvisoryFee.minimumFee) && !isNaN(this.customAdvisoryFee.maximumFee)) {
			isWithinMinFeeConstraint = roundedFee >= this.customAdvisoryFee.minimumFee;
			isWithinMaxFeeConstraint = roundedFee <= this.customAdvisoryFee.maximumFee;
		}

		this.isAdvisoryFeeValid = isWithinMinFeeConstraint && isWithinMaxFeeConstraint;
		this.isAdvisoryFeeEmpty = isNil(roundedFee);
	}

}
